// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMResponseRequiredModalFormComponent } from './lm-response-required-modal-form.component';
import { AppConstants } from '@utils/app.constants';
import { LMModel } from '@form/model/lm-model';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMModelField } from '@form/model/lm-model-field';
import { LMFieldUtilService } from '@services/utils/lm-field-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { Observable } from 'rxjs';
import { LMDialogButtonKey } from '@utils/app.constants';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import {
  LMContextService,
  LMModalDialogWrapperRef,
  LMModalWrapperService,
  LMUrlInfoBuilderService
} from '@services/index';

export enum ResponseRequiredDialogResult {
  RESPONSE_CANCEL = 0,
  RESPONSE_OK,
  RESPONSE_OKTOALL
}

export interface ResponseRequiredResult {
  result: ResponseRequiredDialogResult;
  specFields?: LMSpecFields;
  model?: LMModel;
}

export interface ResponseRequiredDialogOptions {
  dataView: LMDataView;
  actionSpec?: LMActionSpec;
  pfAction?: LMProcessFlowAction;
  busClassSpec?: LMBusClassSpec;
  busFormSpec?: LMBusFormSpec;
  navigationContext?: LMNavContextKeys;
  okToAll?: boolean;  // Render Ok to all button?
}

interface LMFormSpecParams {
  formUrlInfo: LMFormUrlInfo,
  formSpec: LMFormSpec,
}

@Injectable()
export class LMResponseRequiredService {
  constructor(
    private lmContextService: LMContextService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmFieldUtilService: LMFieldUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmModalWrapperService: LMModalWrapperService
  ) {}

  private dialog: LMModalDialogWrapperRef<LMResponseRequiredModalFormComponent> = null; // reference to the most recently opened dialog
  private buttons: SohoModalButton[];
  private dlgComponent: LMResponseRequiredModalFormComponent;

  /***
   * Check if additional response is required.
   * @param actionSpec
   * @param busFormSpec
   * @returns {boolean}
   */
  public isResponseRequired(actionSpec: LMActionSpec, busFormSpec?: LMBusFormSpec, dataView?: LMDataView): boolean {
    if (!actionSpec) {
      return false;
    }

    // odd spec behavior. running local does not add effectiveDateFieldId to form, does if running in grid. //LMClient-26866
    // this should also account for a list with an effective date column that is updateable!
    if (actionSpec.effectiveDateRequired && (!busFormSpec || !busFormSpec.effectiveDateFieldId)
      && this.isEffectiveDateRequired(dataView)) {
      return true;
    }

    if (actionSpec.reasonCodeRequired && (!busFormSpec || !busFormSpec.reasonCodeFieldId)) {
      return true;
    }
    if (actionSpec.actionCommentRequired && (!busFormSpec || !busFormSpec.actionCommentFieldId)) {
      return true;
    }
    if (actionSpec.effectiveThruRequired && (!busFormSpec || !busFormSpec.effectiveThroughFieldId)) {
      return true;
    }
    if (actionSpec.correctionCmtRequired && (!busFormSpec || !busFormSpec.correctionCommentFieldId)) {
      return true;
    }
    return false;
  }

  private isEffectiveDateRequired(dataView: LMDataView): boolean {
    if (!dataView) {
      return true;
    }

    const effectiveDateFld = dataView.fields[`${dataView.busClassName}_effective_date`];
    const effectiveDate = effectiveDateFld ? effectiveDateFld.value : '';
    return this.lmFieldUtilService.isBlank(AppConstants.YYYYMMDD, effectiveDate);
  }

  /**
   * Prompt response required dialog
   * @param      args
   * @return
   */
  public promptResponseRequiredDialog(args: ResponseRequiredDialogOptions): Observable<ResponseRequiredResult> {
    const params: LMFormSpecParams = this.buildResponseRequiredDlgParams(args);

    const formDataView = JSON.parse(JSON.stringify(args.dataView));
    formDataView.action = args.actionSpec.name;

    return this.openDialog(params.formUrlInfo, params.formSpec, formDataView, args.okToAll);
  }

  private buildResponseRequiredDlgParams(args: ResponseRequiredDialogOptions): LMFormSpecParams {
    const actionSpec = args.actionSpec;
    const busFormSpec = args.busFormSpec || null;
    const dataView: LMDataView = args.dataView;
    const formSpec = this.buildBasicFormSpec(args.busClassSpec);

    // set automation id to be used for the modal and generated response required form.
    formSpec.automationId = `ResponseRequired`;

    // let formUrlInfo.navi = {
    //   navigationContext: args.navigationContext,
    //   pfActionName: args.pfAction ? args.pfAction.name : undefined,
    // };

    // Build Spec fields collection
    // And Build layout elements fields collection.
    // TODO: Translate labels. Equivalent of globalize.localize.
    // Effective Date
    if (actionSpec.effectiveDateRequired && (busFormSpec == null || busFormSpec.effectiveDateFieldId == null)) {
      const specField: LMSpecField = <LMSpecField>{
        bindId: dataView.busClassName + '_effective_date',
        dsplSize: 8,
        label: { resourceValue: Soho.Locale.translate('EffectiveDate') },
        size: 8,
        type: AppConstants.YYYYMMDD,
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      const layoutField: LMFormLayoutField = {
        bindId: specField.bindId,
        label: { label: { resourceValue: Soho.Locale.translate('EffectiveDate') } },
        required: true,
        type: 'date',
      };
      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField);
    }

    // Reason Code
    if (actionSpec.reasonCodeRequired
      && (busFormSpec == null || busFormSpec.reasonCodeFieldId == null)) {
      const specField: LMSpecField = <LMSpecField>{
        bindId: dataView.busClassName + '_reason_code',
        drillable: true,
        dsplSize: 20,
        label: { resourceValue: Soho.Locale.translate('Reason') },
        selectable: true,
        selectFieldId: 'ActionReason',
        size: 20,
        type: AppConstants.ALPHAUPPER,
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      let selectListUrl;
      if (!this.lmContextService.useNewSpecUrls()) {
        selectListUrl = this.lmUrlUtilService.getBaseUrl()
          + '/list/ActionReason.ActionReasonSelect?type=select&select='
          + dataView.busClassName + '.' + specField.bindId
          + '&output=spec';
        selectListUrl = this.lmUrlUtilService.propagateSessionKeys(selectListUrl, '&');
      }

      const selectListInfo = {
        selectListResourceId: 'ActionReason.ActionReasonSelect',
        selectResourceType: 'select',
        selectResourceId: dataView.busClassName + '.' + specField.bindId,
      };

      const layoutField: LMLookupLayout = {
        bindId: specField.bindId,
        label: { label: { resourceValue: Soho.Locale.translate('Reason') } },
        required: true,
        selectable: true,
        selectFieldId: 'ActionReason',
        selectListInfo,
        selectListUrl,
        type: 'text',

        // todo spec-context-refactor - drillPointsParam.context should be no diff than global context.
        // const drillResource = new DrillResource({ busClassName: drillPointsParams.dataView.resourceId, drillName: '_drillpoints_' });
        // const url = this.urlUtilService.buildUrl({ context: drillPointsParams.context, resource: drillResource });
      };
      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField);
    }

    // Action Comment
    if ((actionSpec.reasonCodeRequired || actionSpec.actionCommentRequired)
      && (busFormSpec == null || busFormSpec.actionCommentFieldId == null)) {
      const required = (actionSpec.actionCommentRequired) ? true : false; // actionCommentRequired can be undefined
      const specField: LMSpecField = <LMSpecField>{
        type: AppConstants.TEXT,
        bindId: dataView.busClassName + '_action_comment',
        label: { resourceValue: Soho.Locale.translate('ActionComment') }
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      const layoutField: LMFormLayoutField = {
        bindId: specField.bindId,
        label: { label: { resourceValue: Soho.Locale.translate('ActionComment') } },
        lines: 0,
        required,
        type: 'textarea',
      };
      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField, required);
    }

    // Effective Through
    if (actionSpec.effectiveThruRequired
      && (busFormSpec == null || busFormSpec.effectiveThroughFieldId == null)) {
      const specField: LMSpecField = <LMSpecField>{
        size: 8,
        dsplSize: 8,
        type: AppConstants.YYYYMMDD,
        bindId: dataView.busClassName + '_effective_thru',
        label: { resourceValue: 'Effective Through' }
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      const layoutField: LMFormLayoutField = {
        bindId: specField.bindId,
        label: { label: { resourceValue: 'Effective through' } },
        required: true,
        type:  'date',
      };
      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField);
    }

    // Correction Comment
    if (actionSpec.correctionCmtRequired
      && (busFormSpec == null || busFormSpec.correctionCommentFieldId == null)) {
      const specField: LMSpecField = <LMSpecField>{
        bindId: dataView.busClassName + '_correction_cmt',
        label:  { resourceValue: 'Correction Comment' },
        type:   AppConstants.TEXT,
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      const layoutField: LMFormLayoutField = {
        bindId: specField.bindId,
        label: { label: { resourceValue: 'Correction Comment' } },
        lines: 0,
        required: true,
        type:  'textarea',
      };
      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField);
    }

    const formUrlInfo: LMFormUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(formSpec);
    // formUrlInfo.navigationContext = args.navigationContext;
    // formUrlInfo.pfActionName = args.pfAction ? args.pfAction.name : undefined;

    // let formUrlInfo = {
    //   navigationContext: args.navigationContext,
    //   pfActionName: args.pfAction ? args.pfAction.name : undefined,
    // };

    return { formUrlInfo, formSpec };
  }

  private buildBasicFormSpec(busClassSpec: LMBusClassSpec): LMFormSpec {
    // const busFormSpec = args.busFormSpec || null;
    // const dataView: LMDataView = args.dataView;

    const formName = 'RequiredResponse';
    const formSpec: LMFormSpec = {
      isImplicit: true,
      resourceId: busClassSpec.name + '.' + formName,
      formElementId: formName,
      busClassSpec,
      busFormSpec: {
        name: 'ResponseRequired',
        title: { resourceValue: Soho.Locale.translate('ResponseRequired') },
        formType: 'Standard',
        subFormType: 'ResponseReqd',
        fields: {},
        busFormLayoutSpec: {
          layout: {
            sections: [ {
              type: 'column',
              nbrColumns: 1,
              columns: [ {
                elements: []
              } ]
            } ]
          }
        }
      }
    };

    // formSpec.isImplicit = true;
    // formSpec.busFormSpec.name = 'ResponseRequired';
    // // Set the form title
    // formSpec.busFormSpec.title = { 'resourceValue': Soho.Locale.translate('ResponseRequired') };
    // // Set the business class name
    // formSpec.busClassSpec = busClassSpec;

    // if (args.navigationContext) {
    //   formSpec.navigationContext = args.navigationContext;
    // }
    //
    // if (args.pfAction) {
    //   formSpec.pfActionName = args.pfAction.name;
    // }
    return formSpec;
  }

  private addFieldToDV(dataView: LMDataView, specField: LMSpecField, fieldRequired = true): void {
    const dvField: LMField = dataView.fields[ specField.bindId ];
    if (!dvField) {
      dataView.fields[ specField.bindId ] = <LMField>{
        value:   '', size: specField.size,
        mutable: true, required: fieldRequired, label: ''
      }; // Label is required for some reason?
    }
  }

  private openDialog(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    dataView: LMDataView,
    okToAll: boolean
  ): Observable<ResponseRequiredResult> {
    return new Observable<ResponseRequiredResult>(observer => {
      const dialogId = 'ResponseRequired';

      // Build the buttons array
      this.buttons = [
        {
          id: this.lmUniqueIdService.uniqueId([ formSpec.busFormSpec.name, dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: () => {
            this.dialog.close(<ResponseRequiredResult> {
              result: ResponseRequiredDialogResult.RESPONSE_CANCEL
            });
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ formSpec.busFormSpec.name, dialogId, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          validate : false,
          isDefault: true,
          click: () => {
            if (this.validateResponseRequiredForm(formSpec.busFormSpec, this.dlgComponent.formComponent.formModel)) {
              this.dialog.close(<ResponseRequiredResult> {
                result: ResponseRequiredDialogResult.RESPONSE_OK,
                specFields: formSpec.busFormSpec.fields,
                model: this.dlgComponent.formComponent.formModel
              });
            }
          }
        }
      ];
      const defaultButton = this.buttons.find(button => button.isDefault);

      if (okToAll) {
        this.buttons.splice(1, 0, {
          id: this.lmUniqueIdService.uniqueId([ formSpec.busFormSpec.name, dialogId, LMDialogButtonKey.OKTOALL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OKTOALL),
          click: () => {
            this.dialog.close(<ResponseRequiredResult> {
              result: ResponseRequiredDialogResult.RESPONSE_OKTOALL,
              specFields: formSpec.busFormSpec.fields,
              model: this.dlgComponent.formComponent.formModel
            });
          }
        });
      }

      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: `lm-${formSpec.automationId}-modal` } ];
      const modalOptions: SohoModalOptions = {
        title:    Soho.Locale.translate('ResponseRequired'),
        buttons:  this.buttons,
        fullsize: 'responsive',
        isAlert:  false,
        attributes
      };

      this.dialog = this.lmModalWrapperService
        .modal(LMResponseRequiredModalFormComponent, modalOptions)
        .open();

      // dialog = this.sohoModalDialogService
      //   .modal(LMResponseRequiredModalFormComponent, null, {fullsize: 'responsive'})
      //   .buttons(this.buttons)
      //   .title(Soho.Locale.translate('ResponseRequired'))
      //   .isAlert(false)
      //   .open();

      this.dialog.apply((responseReqdForm: LMResponseRequiredModalFormComponent) => {
        this.dlgComponent = responseReqdForm;
        responseReqdForm.formUrlInfo = formUrlInfo;
        responseReqdForm.formSpec = formSpec;
        responseReqdForm.dataView = dataView;
        responseReqdForm.formOptions = {};
        responseReqdForm.defaultButton = defaultButton;
      });

      this.dialog.afterClose((result: ResponseRequiredResult) => {
        if (result === undefined) {
          result = {
            // user hit escape
            result: ResponseRequiredDialogResult.RESPONSE_CANCEL
          };
        }
        this.dlgComponent = null;
        this.dialog = null;
        observer.next(result);
        observer.complete();
      });
    });
  }

  private validateResponseRequiredForm(busFormSpec: LMBusFormSpec, lmModel: LMModel): boolean {
    const keys: string[] = Object.keys(busFormSpec.fields);
    for (let i = 0, l = keys.length; i < l; i++) {
      const bindId: string = keys[ i ];
      const specField: LMSpecField = busFormSpec.fields[ bindId ];
      const modelField: LMModelField = lmModel.getField(bindId);

      if (modelField.required && modelField.isBlank) {
        const message = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FieldIsRequired'), [ specField.label.resourceValue ]);
        lmModel.setFieldError(bindId, message);
        return false;
      }
    }
    return true;
  }

  public isResponseRequiredPF(pfAction: LMProcessFlowAction) {
    const pfActionShowPopup = pfAction.showPopup || false;
    const pfActionReason = pfAction.actionReasonRequired || false;
    const pfActionComment = pfAction.reasonCommentRequired || false;

    return pfActionShowPopup || pfActionReason || pfActionComment;
  }

  public promptResponseRequiredDialogPF(args: ResponseRequiredDialogOptions): Observable<ResponseRequiredResult> {
    // Clone the dataView. Two fields will be added to the dataview for reasoncode and action comment.
    // On ok handler will read the values from the model and push it into the original dataview's pfAction object.
    const formDataView = JSON.parse(JSON.stringify(args.dataView));
    const formSpecResponse: LMFormSpecResponse = this.buildResponseRequiredPFDlgParams(args, formDataView);

    if (args.actionSpec) {
      formDataView.action = args.actionSpec.name;
    }
    return this.openDialog(formSpecResponse.formUrlInfo, formSpecResponse.formSpec, formDataView, args.okToAll);
  }

  private buildResponseRequiredPFDlgParams(args: ResponseRequiredDialogOptions, dataView: LMDataView): LMFormSpecParams {
    const formSpec = this.buildBasicFormSpec(args.busClassSpec);
    const pfAction = args.pfAction;
    const pfActionShowPopup = pfAction.showPopup || false;
    const pfActionReason = pfAction.actionReasonRequired || false;
    const pfActionComment = pfAction.reasonCommentRequired || false;
    const busClassName = pfAction.busClassName || dataView.busClassName;

    // Build Reason code field
    if (pfActionShowPopup || pfActionReason) {
      const specField: LMSpecField = <LMSpecField>{
        bindId: busClassName + '_reason_code_pf',
        drillable: true,
        dsplSize: 20,
        label: { resourceValue: Soho.Locale.translate('Reason') },
        size: 20,
        selectFieldId: 'ActionReason',
        selectable: true,
        type: AppConstants.ALPHAUPPER,
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      let selectUrl;
      if (!this.lmContextService.useNewSpecUrls()) {
        selectUrl = this.lmUrlUtilService.getBaseUrl()
          + '/list/ActionReason.ActionReasonSelect?type=select&select='
          + busClassName + '.PFReasonCode'
          + '&output=spec';
        selectUrl = this.lmUrlUtilService.propagateSessionKeys(selectUrl, '&');
      }

      const selectListInfo = {
        selectListResourceId: 'ActionReason.ActionReasonSelect',
        selectResourceType: 'select',
        selectResourceId: busClassName + '.PFReasonCode',
      };

      const layoutField: LMLookupLayout = {
        bindId: busClassName + '_reason_code_pf',
        label: { label: { resourceValue: Soho.Locale.translate('Reason') } },
        required: pfActionReason,
        selectFieldId: 'ActionReason',
        selectable: true,
        selectListInfo,
        selectListUrl: selectUrl,
        type: 'text',
      };

      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField, pfActionReason);
    }

    if (pfActionShowPopup || pfActionReason || pfActionComment) {
      const specField: LMSpecField = <LMSpecField>{
        bindId: busClassName + '_action_comment_pf',
        label:  { resourceValue: Soho.Locale.translate('ActionComment') },
        type:   AppConstants.TEXT,
      };
      formSpec.busFormSpec.fields[ specField.bindId ] = specField;

      const layoutField: LMFormLayoutField = {
        bindId: busClassName + '_action_comment_pf',
        label: { label: { resourceValue: Soho.Locale.translate('ActionComment') } },
        lines: 0,
        required: pfActionComment,
        type: 'textarea',
      };
      formSpec.busFormSpec.busFormLayoutSpec.layout.sections[ 0 ].columns[ 0 ].elements.push({ field: layoutField });
      this.addFieldToDV(dataView, specField, pfActionComment);
    }

    const formUrlInfo: LMFormUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(formSpec);
    formUrlInfo.navigationContext = args.navigationContext;
    formUrlInfo.pfActionName = args.pfAction ? args.pfAction.name : undefined;

    // const formUrlInfo = {
    //   navigationContext: args.navigationContext,
    //   pfActionName: args.pfAction.name || undefined,
    // };

    return { formUrlInfo, formSpec };
  }
}
